// src/components/NewsGridThreePlay.jsx
import React, { useState, useCallback } from "react";
import SectionHeader from "./SectionHeader";
import PostMeta from "./PostMeta";
import { imgOrPlaceholder } from "../utils/format";
import { useTranslation } from "../i18n";
import { withLangPrefix } from "../lib/i18nRoutes";

/*
 Props:
  - title: string
  - articles: array of article objects (same shape as your API)
  - compact: boolean (smaller first card)
  - showMeta: boolean (show author/date)
  - maxItems: number (how many cards to render)
*/

const FALLBACK = (w, h) => `https://picsum.photos/${w}/${h}`;
const SAFE_LOCALES = ["en", "fr", "ar", "de"];
const DEFAULT_LANG = "en";

/* ------------ i18n helpers (reusable) ------------- */
function cleanTranslatedString(v) {
  if (typeof v !== "string") return v;
  return v.replace(/\s*Translated\s*\([^)]+\)\s*$/i, "").trim();
}

function getTranslatedField(item, field, lang) {
  if (!item) return null;
  const L = (lang || DEFAULT_LANG).toLowerCase().split(/[-_]/)[0];

  // 1) translations array style: [{ target_lang: 'ar', title: '...' }, ...]
  if (Array.isArray(item.translations)) {
    const found = item.translations.find(
      (tr) => tr?.target_lang?.toLowerCase().startsWith(L)
    );
    if (found && found[field]) return cleanTranslatedString(found[field]);
  }

  // 2) object style: item.translated?.ar?.title
  if (item.translated?.[L]?.[field]) {
    return cleanTranslatedString(item.translated[L][field]);
  }

  // 3) suffix style: title_ar
  const suffix = `${field}_${L}`;
  if (item[suffix]) return cleanTranslatedString(item[suffix]);

  // 4) fallback to original field
  return cleanTranslatedString(item[field] ?? null);
}

function normalizeArticleForLang(article, lang) {
  if (!article || typeof article !== "object") return article;
  const a = { ...article };

  const tTitle = getTranslatedField(a, "title", lang);
  const tExcerpt = getTranslatedField(a, "excerpt", lang);
  const tImage = getTranslatedField(a, "image", lang) ?? getTranslatedField(a, "thumbnail", lang);
  const tCategory = getTranslatedField(a, "category", lang);

  a._orig = a._orig || {
    title: article.title,
    excerpt: article.excerpt,
    category: article.category,
    image: article.image ?? article.thumbnail,
  };

  a._translatedTitle = tTitle ?? null;
  a._translatedExcerpt = tExcerpt ?? null;
  a._translatedCategory = tCategory ?? null;
  a._translatedImage = tImage ?? null;

  if (tTitle) a.title = tTitle;
  if (tExcerpt) a.excerpt = tExcerpt;
  if (tCategory) a.category = tCategory;
  if (tImage) a.image = tImage;

  const slug = a.slug ?? "";
  const hrefPath =
    typeof a.url === "string" && a.url.startsWith("/articles/") ? a.url : `/articles/${encodeURIComponent(slug)}`;
  a.href = withLangPrefix(hrefPath, lang);
  a.image_url = a.image_url ?? a.image ?? a.thumbnail ?? null;

  return a;
}

/* ------------ utility ------------- */
function detectVideoProvider(url) {
  if (!url) return null;
  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
  if (url.includes("vimeo.com")) return "vimeo";
  return "html5";
}

/* ------------ Video modal ------------- */
function VideoModal({ open, onClose, src, type, title }) {
  if (!open) return null;
  return (
    <div className="ng3p-modal" role="dialog" aria-modal="true" aria-label={`Video player: ${title}`}>
      <div className="ng3p-modal-backdrop" onClick={onClose} />
      <div className="ng3p-modal-content">
        <button className="ng3p-modal-close btn-close" aria-label="Close video" onClick={onClose} />
        {type === "html5" && (
          <video controls autoPlay style={{ width: "100%", height: "100%" }}>
            <source src={src} />
            Your browser does not support the video element.
          </video>
        )}
        {type === "youtube" && (
          <iframe
            src={src}
            title={title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: "100%", height: "100%" }}
          />
        )}
        {type === "vimeo" && (
          <iframe
            src={src}
            title={title}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{ width: "100%", height: "100%" }}
          />
        )}
      </div>
    </div>
  );
}

/* ------------ Enhanced PlayCard (polished design) ------------- */
const PlayCard = ({ a, compact, onOpenVideo, showMeta = true, lang }) => {
  if (!a) return null;

  const article = normalizeArticleForLang(a, lang);
  const displayedTitle = article._translatedTitle ?? article.title ?? "—";
  const displayedExcerpt = article._translatedExcerpt ?? article.excerpt ?? "";
  const displayedCategory = article._translatedCategory ?? article.category ?? null;
  const displayedImage = article.image_url ?? article.image ?? article.thumbnail ?? FALLBACK(640, 400);

  const link = article.href ?? withLangPrefix(`/articles/${article.slug ?? article.id ?? ""}`, lang);
  const height = compact ? 240 : 320;
  const [w, h] = compact ? [324, 235] : [640, 400];

  const img = displayedImage || FALLBACK(w, h);
  const hasVideo = !!(article.video_url || article.media_type === "video");
  const duration = article.duration || article.video_duration || null;
  const videoType = article.video_provider || detectVideoProvider(article.video_url);

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      if (hasVideo) onOpenVideo(article, videoType);
      else (window.location.href = link);
      e.preventDefault();
    }
  };

  return (
    <div className="col-12 col-md-4">
      <div
        role="button"
        tabIndex={0}
        className="enh-card d-block position-relative rounded-4 overflow-hidden text-white tilt"
        style={{ height }}
        onClick={() => (hasVideo ? onOpenVideo(article, videoType) : (window.location.href = link))}
        onKeyDown={onKey}
        aria-label={displayedTitle}
      >
        <img
          src={imgOrPlaceholder(img, w, h)}
          alt={displayedTitle}
          loading="lazy"
          className="enh-img"
          onError={(e) => (e.currentTarget.src = FALLBACK(w, h))}
        />

        {/* Category badge */}
        {displayedCategory && (
          <div className="enh-top-left" aria-hidden>
            <span className="enh-badge" title={displayedCategory}>{displayedCategory}</span>
          </div>
        )}

        {/* Play CTA */}
        {hasVideo && (
          <button
            type="button"
            className="enh-play"
            aria-label={`Play video: ${displayedTitle}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenVideo(article, videoType);
            }}
          >
            <span className="enh-play-ring" aria-hidden />
            <i className="bi bi-play-fill" aria-hidden />
          </button>
        )}

        {/* Duration */}
        {duration && <div className="enh-duration" aria-hidden>{duration}</div>}

        {/* Bottom glass panel */}
        <div className="enh-bottom-panel" aria-hidden={false}>
          <div className="enh-bottom-inner">
            <div className="enh-texts">
              <h6 className="enh-title" title={displayedTitle}>{displayedTitle}</h6>
              {displayedExcerpt && <p className="enh-excerpt">{displayedExcerpt}</p>}
            </div>

           <div className="enh-meta" style={{ color: "#ffffff" }}>
  {showMeta && <PostMeta date={article.published_at || article.created_at} locale={lang} />}
  {article.author?.username && <div className="enh-author">By {article.author.username}</div>}
</div>

          </div>
        </div>
      </div>

      {/* Scoped CSS */}
      <style>{`
        .enh-card {
          cursor: pointer;
          transition: transform .35s cubic-bezier(.2,.9,.3,1), box-shadow .35s ease;
          border: 1px solid rgba(0,0,0,0.06);
          background: linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.02));
        }
        .enh-card:focus-visible { outline: none; box-shadow: 0 0 0 .28rem rgba(116,198,157,0.18); transform: translateY(-6px); }
        .enh-card:hover { transform: translateY(-8px); box-shadow: 0 26px 64px rgba(2,6,23,0.14); }

        .enh-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform .6s cubic-bezier(.2,.9,.3,1), filter .35s ease;
          will-change: transform;
        }
        .enh-card:hover .enh-img { transform: scale(1.08); filter: saturate(1.05) contrast(1.02); }

        .enh-top-left { position: absolute; left: 12px; top: 12px; z-index: 4; }
        .enh-badge {
          background: linear-gradient(90deg, rgba(116,198,157,1), rgba(149,213,178,1));
          color: #fff;
          font-weight: 700;
          font-size: .68rem;
          padding: 6px 10px;
          border-radius: 999px;
          box-shadow: 0 6px 18px rgba(116,198,157,0.16);
          max-width: 140px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: inline-block;
        }

        .enh-play {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 68px;
          height: 68px;
          border-radius: 50%;
          border: none;
          display: grid;
          place-items: center;
          background: rgba(0,0,0,0.42);
          backdrop-filter: blur(6px);
          z-index: 5;
          transition: transform .22s ease, background .22s ease;
        }
        .enh-play:focus { outline: none; box-shadow: 0 0 0 .22rem rgba(255,255,255,0.12); }
        .enh-play:hover { transform: translate(-50%, -50%) scale(1.06); background: rgba(0,0,0,0.56); }
        .enh-play i { color: #fff; font-size: 1.55rem; margin-left: 2px; }

        .enh-play-ring {
          position: absolute;
          inset: -6px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.14);
          animation: enh-pulse 1.8s infinite ease-out;
        }
        @keyframes enh-pulse {
          0%{ transform: scale(1); opacity: .9; }
          70%{ transform: scale(1.6); opacity: 0; }
          100%{ transform: scale(1.6); opacity: 0; }
        }

        .enh-duration {
          position: absolute;
          right: 10px;
          top: 12px;
          z-index: 6;
          background: rgba(0,0,0,0.6);
          padding: 4px 8px;
          color: #fff;
          font-size: .75rem;
          border-radius: 12px;
          backdrop-filter: blur(4px);
        }

        .enh-bottom-panel {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 12px;
          z-index: 6;
          background: linear-gradient(180deg, rgba(0,0,0,0.0), rgba(0,0,0,0.45));
          display: flex;
          align-items: flex-end;
        }
        .enh-bottom-inner {
          width: 100%;
          display:flex;
          gap: 12px;
          align-items: flex-end;
          justify-content: space-between;
        }

        .enh-texts { flex: 1 1 60%; min-width: 0; }
        .enh-title {
          margin: 0 0 6px 0;
          font-size: 1rem;
          font-weight: 700;
          color: #fff;
          line-height: 1.1;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .enh-excerpt {
          margin: 0;
          color: rgba(255,255,255,0.85);
          font-size: .85rem;
          opacity: .95;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .enh-meta {
          display:flex;
          flex-direction: column;
          gap: 6px;
          align-items: flex-end;
          min-width: 0;
        }
        .enh-author {
          color: rgba(255,255,255,0.9);
          font-size: .78rem;
        }

        /* existing tilt styles fallback */
        .tilt { will-change: transform; }

        /* modal + skeleton (kept from original) */
        .ng3p-mask{ position:absolute; inset:0; background: linear-gradient(to top, rgba(0,0,0,.72), rgba(0,0,0,.0) 55%); }
        .ng3p-modal { position: fixed; inset: 0; z-index: 1500; display:flex; align-items:center; justify-content:center; }
        .ng3p-modal-backdrop { position:absolute; inset:0; background: rgba(0,0,0,.6); }
        .ng3p-modal-content { position: relative; width: min(1100px, 95%); max-height: 80vh; background: #000; border-radius: 8px; overflow: hidden; padding: 12px; box-shadow: 0 20px 70px rgba(0,0,0,.6); }
        .ng3p-modal .btn-close { position: absolute; right: 8px; top: 8px; z-index: 10; }
        .ng3p-skeleton { background: linear-gradient(90deg,#eee,#f5f5f5,#eee); height:100%; border-radius:12px; box-shadow: none; }

        @media (max-width: 575.98px) {
          .enh-play { width: 56px; height: 56px; }
          .enh-title { font-size: .95rem; }
          .enh-badge { font-size: .65rem; padding: 5px 8px; }
        }
      `}</style>
    </div>
  );
};

/* ------------ Skeleton ------------- */
const SkeletonCard = ({ compact = false }) => {
  const height = compact ? 240 : 320;
  return (
    <div className="col-12 col-md-4">
      <div className="skeleton ng3p-skeleton rounded-4" style={{ height }} />
    </div>
  );
};

/* ------------ Main component ------------- */
const NewsGridThreePlay = ({ title = "Videos", articles = [], compact = false, showMeta = true, maxItems = 3 }) => {
  const { lang: providerLang, t } = useTranslation();

  // safe language
  const candidate = providerLang || DEFAULT_LANG;
  const safeLang = SAFE_LOCALES.includes(candidate) ? candidate : DEFAULT_LANG;

  // normalize visible articles
  const visible = (articles || []).slice(0, maxItems).map((it) => normalizeArticleForLang(it, safeLang));
  const [a0, a1, a2] = visible;

  const [openVideo, setOpenVideo] = useState(null); // {article, type, src}
  const [isOpen, setIsOpen] = useState(false);

  const onOpenVideo = useCallback((article, detectedType) => {
    if (!article) return;
    let src = article.video_url || article.embed_url || null;

    if (detectedType === "youtube" && src) {
      try {
        const id = (function getYid(u){
          const m = u.match(/(?:v=|\/embed\/|youtu\.be\/|\/v\/)([^&?/]+)/);
          return m ? m[1] : null;
        })(src);
        if (id) src = `https://www.youtube.com/embed/${id}?autoplay=1`;
      } catch {}
    } else if (detectedType === "vimeo" && src) {
      try {
        const id = (function getVid(u){
          const m = u.match(/vimeo\.com\/(?:video\/)?(\\d+)/);
          return m ? m[1] : null;
        })(src);
        if (id) src = `https://player.vimeo.com/video/${id}?autoplay=1`;
      } catch {}
    }

    const type = detectedType || (article.video_url && (article.video_url.includes("youtube") ? "youtube" : article.video_url.includes("vimeo") ? "vimeo" : "html5"));
    setOpenVideo({ article, src, type });
    setIsOpen(true);
  }, []);

  const onCloseVideo = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setOpenVideo(null), 220);
  }, []);

  return (
    <section className="mb-5" aria-label={t(title) || title}>
      <SectionHeader
        title={t(title) || title}
        underline
        icon="bi-youtube"
        accent="danger"
        right={
          <>
            <button className="btn btn-sm btn-outline-secondary" aria-label="Grid">
              <i className="bi bi-grid-3x3-gap" />
            </button>
            <button className="btn btn-sm btn-outline-secondary" aria-label="List">
              <i className="bi bi-list-task" />
            </button>
          </>
        }
      />

      <div className="row g-3 g-lg-4">
        {visible.length === 0 ? (
          <>
            <SkeletonCard compact={compact} />
            <SkeletonCard compact />
            <SkeletonCard compact />
          </>
        ) : (
          <>
            <PlayCard a={a0} compact={compact} onOpenVideo={onOpenVideo} showMeta={showMeta} lang={safeLang} />
            <PlayCard a={a1} onOpenVideo={onOpenVideo} showMeta={showMeta} lang={safeLang} />
            <PlayCard a={a2} onOpenVideo={onOpenVideo} showMeta={showMeta} lang={safeLang} />
          </>
        )}
      </div>

      <VideoModal
        open={isOpen}
        onClose={onCloseVideo}
        src={openVideo?.src}
        type={openVideo?.type}
        title={(openVideo?.article?._translatedTitle ?? openVideo?.article?.title) || ""}
      />
    </section>
  );
};

export default NewsGridThreePlay;
